// Libraries

import { DocumentIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { signAsset } from '@/api/assets';
// Assets
import { getLatestCreatedDraw, patchDraw } from '@/api/Sherpa/Draw';
import type { PatchDrawPayload } from '@/api/Sherpa/Draw/types';
import Button from '@/components/UI/Button';
// custom components
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactMultiSelect, HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { useFileUpload } from '@/hooks/useFileUpload';
import { notifyUser } from '@/store/Toast';
import type { UserOption } from '@/types/Constants';
import { byteConverter, daysCountV2, formatDate2, formatDate3, getAcceptedFileTypes, sanitizeResponseError } from '@/utils';
import type { TFile } from '../DrawDetails/Documents/EditDocument';
import { isDrawApproverOptionEqual, useDrawApproverOptions } from '../hooks/useDrawApproverOptions';
import { validationSchema } from '../NewDraw/validationSchema';

interface EditDrawProps {
  handleModal: () => void;
  projectName: string;
  draw: any;
}

const EditDrawBase = ({ handleModal, projectName, draw }: EditDrawProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { projectId, drawId } = useParams();
  const { options: approverOptions, isLoading: isApproverOptionsLoading } = useDrawApproverOptions(Number(projectId));

  const transformDataRef = useRef<any>(null);

  const { templateDocuments } = draw;

  const [recentFiles, setRecentFiles] = useState<TFile[]>(templateDocuments);

  const { watch, setValue } = useFormContext();
  const openDate = watch('openDate');
  const closeDate = watch('closeDate');

  useEffect(() => {
    const totalDate = closeDate ? daysCountV2(closeDate, openDate) : 0;
    setValue('length', totalDate < 0 ? 0 : totalDate);
  }, [openDate, closeDate, setValue]);

  useEffect(() => {
    if (!draw || approverOptions.length === 0) {
      return;
    }

    setValue('length', daysCountV2(draw.closeDate, draw.openDate));
    setValue('openDate', formatDate3(draw.openDate, 'yyyy-mm-dd'));
    setValue('closeDate', formatDate3(draw.closeDate, 'yyyy-mm-dd'));
    setValue('invoiceApproverId', approverOptions.find((mem) => mem.id === draw.invoiceApproverId) ?? null);
    setValue('documentApproverId', approverOptions.find((mem) => mem.id === draw?.documentApproverId) ?? null);
    setValue(
      'documentViewers',
      approverOptions.filter((mem) => draw?.viewerIds?.includes(mem?.id))
    );
  }, [draw, setValue, approverOptions]);

  const { upload } = useFileUpload({ signAsset: signAsset });

  const updateDraw = useMutation(
    (data: PatchDrawPayload) =>
      patchDraw({
        data,
        projectId: Number(projectId),
        drawId: Number(drawId),
      }),
    {
      onSuccess: () => {
        //Tanstack: version 5 query implementation
        queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_DRAW] });
        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Draw updated successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );
  const uploadFile = useMutation(
    (payload: {
      signAssetPayload: {
        key: string;
        type: string;
        resource: string;
      }[];
      files: File[];
    }) => upload(payload),
    {
      onSuccess: (response) => {
        const assetsFiles = response?.files.map((file: any, index: number) => {
          return {
            name: file.name,
            key: response?.sign.fKey ?? response.sign[index].fKey,
            contentType: file.type,
            size: file.size,
          };
        });

        updateDraw.mutate({
          ...transformDataRef.current,
          templateDocuments: [...assetsFiles, ...(transformDataRef?.current?.templateDocuments || [])],
        });
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const updateDrawHandler = (data: any) => {
    const { templateDocuments } = data;

    const transformedRecentFiles = recentFiles.map(({ contentType, key, name, size }) => ({
      contentType,
      key,
      name,
      size,
    }));

    transformDataRef.current = {
      openDate: formatDate2(data.openDate),
      closeDate: formatDate2(data.closeDate),
      invoiceApproverId: data.invoiceApproverId.id,
      documentApproverId: data.documentApproverId.id,
      viewerIds: data.documentViewers.map((viewer: any) => viewer?.id),
      templateDocuments: transformedRecentFiles,
    };

    if (templateDocuments?.length) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      templateDocuments?.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(',');
        if (acceptedFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
            toastType: 'failed',
          })
        );
      }

      if (validFiles.length > 0) {
        const signPayload = validFiles.map<{
          key: string;
          type: string;
          resource: string;
        }>((file) => ({
          key: file.name,
          type: file.type,
          resource: 'projectDraw',
        }));

        uploadFile.mutate({
          signAssetPayload: signPayload,
          files: validFiles,
        });
      }
    } else {
      updateDraw.mutate(transformDataRef.current);
    }
  };

  //Tanstack: version 5 query implementation
  const fetchLatestDraw = useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_LATEST_DRAW, projectId],
    queryFn: () =>
      getLatestCreatedDraw({
        projectId: Number(projectId),
      }),
  });
  const _latestDraw: { openDate: string; closeDate: string } | undefined = fetchLatestDraw?.data?.data;

  const minDate = (date: string | number | Date | undefined) => {
    if (date) {
      return dayjs(date).utc().startOf('day').add(1, 'day').format('YYYY-MM-DD');
    } else {
      return dayjs().utc().startOf('day').add(1, 'day').format('YYYY-MM-DD');
    }
  };

  const handleRemoveFiles = (file: TFile) => {
    const updatedFiles = recentFiles.filter((f) => f.key !== file.key);
    setRecentFiles(updatedFiles);
  };

  return (
    <HookForm onSubmit={updateDrawHandler} className='space-y-2 grid grid-cols-2 gap-4'>
      <h3 className='text-sm col-span-2'>Project Name: {projectName}</h3>
      <HookInput className='col-span-1' type='date' label='Open Date' name='openDate' required disabled={fetchLatestDraw.isLoading} />
      <HookInput className='col-span-1' type='date' label='Close Date' name='closeDate' min={minDate(openDate)} required />
      <HookInput className='col-span-1' type='text' label='Draw Length (in days)' name='length' disabled />
      <div className='col-span-2'>
        <HookFileInput label='Template Documents' name='templateDocuments' id='template' multiple />
      </div>
      {recentFiles && recentFiles.length > 0 && (
        <div className='col-span-2 space-y-1'>
          <p>Recent Uploads</p>
          <ul>
            {recentFiles.map((file) => (
              <li key={file.createdAt} className='flex justify-between items-center border rounded-md p-2'>
                <div className='flex gap-2 items-center'>
                  <DocumentIcon className='w-7 h-7 flex-none' />
                  <div className='flex flex-col'>
                    <h5 className='text-sm capitalize'>{file.name}</h5>
                    <div className='flex text-xs gap-5 text-neutralColor-normal'>
                      <span>Date: {formatDate3(file.createdAt)}</span>
                      <span>Size: {byteConverter(file.size, 2)}</span>
                    </div>
                  </div>
                </div>
                <XCircleIcon className='w-4 h-4 hover:text-tertiaryColor-normal cursor-pointer' onClick={() => handleRemoveFiles(file)} />
              </li>
            ))}
          </ul>
        </div>
      )}
      <HookReactSingleSelect
        componentClassName='col-span-2'
        label='Invoice Approver'
        name='invoiceApproverId'
        options={approverOptions}
        getOptionLabel={(option) => (option as UserOption).name}
        getOptionValue={(option) => (option as UserOption).id.toString()}
        isOptionEqualToValue={isDrawApproverOptionEqual}
        placeholder='Select...'
        isLoading={isApproverOptionsLoading}
        required
      />
      <HookReactSingleSelect
        componentClassName='col-span-2'
        label='Document Approver'
        name='documentApproverId'
        options={approverOptions}
        getOptionLabel={(option) => (option as UserOption).name}
        getOptionValue={(option) => (option as UserOption).id.toString()}
        isOptionEqualToValue={isDrawApproverOptionEqual}
        placeholder='Select...'
        isLoading={isApproverOptionsLoading}
        required
      />
      <div className='col-span-2'>
        <HookReactMultiSelect
          name='documentViewers'
          label='Document Viewer'
          options={approverOptions}
          getOptionLabel={(option) => (option as UserOption).name}
          getOptionValue={(option) => (option as UserOption).id.toString()}
          isOptionEqualToValue={isDrawApproverOptionEqual}
          placeholder='Select...'
          isLoading={isApproverOptionsLoading}
          required
        />
      </div>
      <div className='flex gap-4 mt-5 justify-end col-span-2'>
        <Button
          label='Cancel'
          type='button'
          btnStyle='btn-neutral'
          disabled={updateDraw.isLoading || uploadFile.isLoading || isApproverOptionsLoading}
          onClick={handleModal}
        />
        <Button
          label={`${updateDraw.isLoading ? 'Updating...' : 'Edit Draw'}`}
          type='submit'
          btnStyle='btn-primary'
          disabled={updateDraw.isLoading || uploadFile.isLoading || isApproverOptionsLoading}
        />
      </div>
    </HookForm>
  );
};

export const EditDraw = (props: EditDrawProps) => {
  return (
    <Modal>
      <h2 className='mt-0'>Edit Draw</h2>
      <HookFormProvider
        resolver={yupResolver(validationSchema) as any}
        defaultValues={{
          openDate: formatDate3(props.draw.openDate, 'yyyy-mm-dd'),
          closeDate: formatDate3(props.draw.closeDate, 'yyyy-mm-dd'),
          invoiceApproverId: null,
          documentApproverId: null,
          documentViewers: [],
        }}
      >
        <EditDrawBase {...props} />
      </HookFormProvider>
    </Modal>
  );
};
